<template>
  <div class="relative">
    <TransitionRoot appear :show="show" as="template">
      <Dialog as="div" @close="close" class="relative z-[9999999999999] w-full">
        <TransitionChild
          as="template"
          enter="duration-300 ease-out"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="duration-200 ease-in"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <div class="fixed inset-0 bg-dark-blue-light" aria-hidden="true" />
        </TransitionChild>

        <div class="fixed inset-0">
          <div class="flex min-h-full items-center justify-center text-center w-full pt-10 md:pt-0">
            <TransitionChild
              as="template"
              enter="duration-300 ease-out"
              enter-from="opacity-0 scale-95"
              enter-to="opacity-100 scale-100"
              leave="duration-200 ease-in"
              leave-from="opacity-100 scale-100"
              leave-to="opacity-0 scale-95"
            >
              <DialogPanel
                id="modal"
                class="w-full max-w-[357px] md:max-w-[1280px] max-h-[85vh] md:max-h-[683px] h-auto transform overflow-y-auto bg-white text-left align-middle shadow-xl transition-all"
              >
                <button
                  @click="close"
                  type="button"
                  class="absolute md:mr-6 md:mt-6 top-4 right-4 z-50 text-gray-400 bg-light-silver rounded-full fill-secondary p-1"
                >
                  <svg aria-hidden="true" class="w-5 h-5" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                    <path
                      fill-rule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                      clip-rule="evenodd"
                    />
                  </svg>
                  <span class="sr-only">Close modal</span>
                </button>

                <div class="w-full max-w-[323px] md:max-w-[1042px] mx-auto md:pt-12 pt-16 relative">
                  <div class="w-full">
                    <div class="flex flex-col md:flex-row items-center md:gap-20 gap-1">
                      <h2
                        class="text-xl md:text-2xl font-bold text-[#253754] text-center md:text-left order-1 md:order-2"
                      >
                        What our customers say about us
                      </h2>
                      <div class="order-2 md:order-1 self-start md:self-center">
                        <IconsReviewModelWallpaper class="w-[130px] h-auto md:w-auto" />
                      </div>
                    </div>
                    <div class="w-full h-1 bg-primary"></div>
                  </div>

                  <div class="md:mt-6 md:pt-6 mt-7 mb-8 md:ml-20 md:mr-20">
                    <div v-if="loadingReviews && reviewsList.length === 0" class="text-center">
                      <p>Loading reviews...</p>
                    </div>
                    <div v-else class="space-y-6">
                      <ReviewsCustomerComment
                        v-for="review in reviewsList"
                        :key="review.customer_name"
                        :stars="Number(review.rating)"
                        :customer-name="review.customer_name"
                        :customer-location="review.location"
                        :comment="review.review"
                      />
                    </div>
                    <div v-if="canLoadMore" class="text-center mt-6">
                      <BaseButton
                        :text="$t('common.btnLoadReviews')"
                        variant="outlined"
                        class="w-fit mx-auto"
                        :loading="loadingReviews"
                        @click="handleLoadMore"
                      />
                    </div>
                  </div>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </TransitionRoot>
  </div>
</template>

<script setup>
import { Dialog, DialogPanel, TransitionChild, TransitionRoot } from "@headlessui/vue";
import { ref } from "vue";
import BaseButton from "~/components/base/Button.vue";
import IconsReviewModelWallpaper from "~/components/icons/ReviewModelWallpaper.vue";
import ReviewsCustomerComment from "~/components/reviews/CustomerComment.vue";
import { useReviewsList } from "~/composables/review";

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["close"]);

const reviewsList = ref([]);
const loadingReviews = ref(false);
const currentPage = ref(1);
const canLoadMore = ref(false);

const loadReviews = async (page = 1) => {
  loadingReviews.value = true;

  try {
    const { list, canLoadMore: newCanLoadMore } = await useReviewsList(page);
    if (page === 1) {
      reviewsList.value = list;
    } else {
      reviewsList.value.push(...list);
    }

    canLoadMore.value = newCanLoadMore;
  } catch (error) {
    console.error("Failed to load reviews:", error);
  } finally {
    loadingReviews.value = false;
  }
};

const handleLoadMore = () => {
  currentPage.value += 1;
  loadReviews(currentPage.value);
};

if (reviewsList.value.length === 0) {
  loadReviews(currentPage.value);
}

const close = () => {
  emit("close");
};
</script>
